// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { xParse, xParserOptions } from "@eisinc/XDL/Utilities";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";

const enum fields {
    fullWidthButton = "fullWidthButton",
    guestCheckoutTitle = "guestCheckoutTitle",
    guestCheckoutText = "guestCheckoutText",
    guestCheckoutButtonText = "guestCheckoutButtonText",
}

const mapStateToProps = (state: ApplicationState) => {
    let { search } = getLocation(state);
    const { allowGuestCheckout } = getSettingsCollection(state).accountSettings;
    let returnUrl = getReturnUrl(state);
    if (!returnUrl && state.components.signInOverlay.isOpen) {
        returnUrl = getLocation(state).pathname;
        if (!search) {
            search = `?returnUrl=${returnUrl}`;
        }
    }

    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const onePageCheckoutPageUrl = getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
    const referredFromOnePageCheckout = returnUrl === onePageCheckoutPageUrl;

    return {
        allowGuestCheckout:
            allowGuestCheckout &&
            !getCurrentUserIsGuest(state) &&
            (referredFromShipping || referredFromOnePageCheckout),
        returnUrl,
    };
};

const mapDispatchToProps = {
    signInAsGuest,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.fullWidthButton]: boolean;
        [fields.guestCheckoutTitle]: string;
        [fields.guestCheckoutText]: string;
        [fields.guestCheckoutButtonText]: string;
    };
}

type Props = ReturnType<typeof mapStateToProps> &
    OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasShellContext;

export interface SignInGuestCheckoutStyles {
    wrapper?: InjectableCss;
    guestCheckoutTitle?: TypographyProps;
    guestCheckoutTextWrapper?: InjectableCss;
    guestCheckoutButton?: ButtonPresentationProps;
}

export const signInGuestCheckoutStyles: SignInGuestCheckoutStyles = {
    guestCheckoutTitle: {
        variant: "h4",
    },
    guestCheckoutTextWrapper: {
        css: css`
            margin-top: 15px;
        `,
    },
    guestCheckoutButton: {
        variant: "secondary",
        css: css`
            margin-top: 20px;
        `,
    },
};

const styles = signInGuestCheckoutStyles;

const SignInGuestCheckout: FC<Props> = ({ fields, allowGuestCheckout, returnUrl, signInAsGuest, shellContext }) => {
    if (!allowGuestCheckout && !shellContext.isEditing) {
        return null;
    }

    const handleCheckoutAsGuest = () => {
        if (!returnUrl) {
            return;
        }

        signInAsGuest({ returnUrl });
    };

    const buttonCss = fields.fullWidthButton
        ? css`
              ${styles.guestCheckoutButton?.css}
              width: 100%;
          `
        : css`
              ${styles.guestCheckoutButton?.css}
          `;

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.guestCheckoutTitle}>{fields.guestCheckoutTitle}</Typography>
            <StyledWrapper {...styles.guestCheckoutTextWrapper}>
                {xParse(fields.guestCheckoutText, xParserOptions)}
            </StyledWrapper>
            <Button
                {...styles.guestCheckoutButton}
                css={buttonCss}
                onClick={handleCheckoutAsGuest}
                data-test-selector="signInGuestCheckout_checkoutAsGuest"
            >
                {translate(fields.guestCheckoutButtonText)}
            </Button>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withHistory(SignInGuestCheckout))),
    definition: {
        allowedContexts: ["SignInPage"],
        group: "Sign In",
        icon: "User",
        fieldDefinitions: [
            {
                name: fields.guestCheckoutTitle,
                displayName: "Column Header",
                editorTemplate: "TextField",
                defaultValue: "Checkout as a Guest",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.guestCheckoutText,
                displayName: "Rich Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 1,
            },
            {
                name: fields.guestCheckoutButtonText,
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "Guest Checkout",
                fieldType: "Translatable",
                sortOrder: 2,
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
        ],
    },
};

export default widgetModule;
